import {Dispatch, PropsWithChildren, useMemo} from "react";
import {
  createInitialCreateStoreServiceReducerStateFromApi,
  createStoreServiceReducer,
} from "../../reducers/create-service";
import {createContext} from "../../../../../context/create-context";
import {
  CreateStoreServiceActions,
  CreateStoreServiceState,
} from "../../reducers/create-service/types";
import {usePricingCurrencies} from "@/features/currencies";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  usePersistedReducer,
} from "../../../../../utils/offering-create-draft";

type CreateStoreServiceContextState = {
  state: CreateStoreServiceState;
  dispatch: Dispatch<CreateStoreServiceActions>;
  wasDraftRestored: boolean;
};

const [useContext, ContextProvider] = createContext<CreateStoreServiceContextState>();

export const useCreateStoreServiceContext = useContext;

export const CreateStoreServiceProvider = ({children}: PropsWithChildren) => {
  const {state: profileState} = useGetProfileContext();
  const pricingCurrencies = usePricingCurrencies();

  // Snapshot values once for seeding.
  // If your currency context is async, guard with a fallback until ready.
  const bankConnected = !!pricingCurrencies.connectedBankStatus.isBankConnected;
  const defaultCurrency = pricingCurrencies.formCurrencies[0];

  const getInitialState = () =>
    createInitialCreateStoreServiceReducerStateFromApi({
      bankConnected,
      defaultCurrency: defaultCurrency ?? "NGN",
    });

  const draftScope = useMemo(
    () => ({
      draftKey: OFFERING_CREATE_DRAFT_KEYS.storeService,
      email: profileState.profileData.email,
    }),
    [profileState.profileData.email]
  );

  const [state, dispatch, {wasDraftRestored}] = usePersistedReducer(
    createStoreServiceReducer,
    draftScope,
    getInitialState
  );
  const value = useMemo(
    () => ({state, dispatch, wasDraftRestored}),
    [state, wasDraftRestored]
  );

  // If currency context isn't ready yet, you can early-return null or a loader
  // to avoid seeding with undefined. Adjust to your app's UX.
  if (!defaultCurrency) {
    return null; // or a loader component
  }

  return <ContextProvider value={value}>{children}</ContextProvider>;
};
